import { categories } from "./categories";
import { listings } from "./listings";
import { Category, Listing, PublisherType, QueryState } from "./types";

/** 信息门户每页条数（对齐参考站分类页） */
export const PAGE_SIZE = 10;

export interface Paged<T> {
  items: T[];
  total: number;
  pageCount: number;
  page: number;
}

export function resolveCategory(slug: string): Category | undefined {
  return categories.find((x) => x.slug === slug);
}

export function categoryName(slug: string): string {
  const category = resolveCategory(slug);
  return category ? category.name : "未分类";
}

export function subCategoryName(categorySlug: string, subSlug: string): string {
  const category = resolveCategory(categorySlug);
  if (!category) return "";
  const sub = category.subCategories.find((x) => x.slug === subSlug);
  return sub ? sub.name : "";
}

/** 排序：置顶优先 → 推荐优先 → 发布日期倒序 → id 升序 */
export function sortListings(list: Listing[]): Listing[] {
  return list.slice().sort((a, b) => {
    if (!!a.top !== !!b.top) return a.top ? -1 : 1;
    if (!!a.recommend !== !!b.recommend) return a.recommend ? -1 : 1;
    if (a.publishedAt !== b.publishedAt) {
      return a.publishedAt < b.publishedAt ? 1 : -1;
    }
    return a.id - b.id;
  });
}

export function filterListings(state: QueryState): Listing[] {
  const keyword = state.q.trim().toLowerCase();
  const matched = listings.filter((item) => {
    if (state.category && item.category !== state.category) return false;
    if (state.type && item.publisherType !== (state.type as PublisherType)) {
      return false;
    }
    if (keyword) {
      const haystack = `${item.title} ${item.summary}`.toLowerCase();
      if (haystack.indexOf(keyword) < 0) return false;
    }
    return true;
  });
  return sortListings(matched);
}

export function paginate<T>(
  list: T[],
  page: number,
  size: number = PAGE_SIZE
): Paged<T> {
  const total = list.length;
  const pageCount = Math.max(1, Math.ceil(total / size));
  const current = Math.min(Math.max(1, Math.floor(page) || 1), pageCount);
  const start = (current - 1) * size;
  return {
    items: list.slice(start, start + size),
    total,
    pageCount,
    page: current,
  };
}

export function getListingById(id: number): Listing | undefined {
  return listings.find((x) => x.id === id);
}

export function listingsByCategory(slug: string, limit?: number): Listing[] {
  const list = sortListings(listings.filter((x) => x.category === slug));
  return typeof limit === "number" ? list.slice(0, limit) : list;
}

export function countByCategory(slug: string): number {
  return listings.filter((x) => x.category === slug).length;
}

function readString(value: unknown): string {
  if (Array.isArray(value)) {
    return value.length ? String(value[0]) : "";
  }
  return value === undefined || value === null ? "" : String(value);
}

/** 把路由 query 解析为受约束的筛选状态（非法值一律回落到默认） */
export function parseQuery(query: Record<string, unknown>): QueryState {
  const category = readString(query.category);
  const type = readString(query.type);
  const page = parseInt(readString(query.page), 10);
  return {
    category: resolveCategory(category) ? category : "",
    type: type === "personal" || type === "merchant" ? type : "",
    q: readString(query.q),
    page: Number.isNaN(page) || page < 1 ? 1 : page,
  };
}

/** 筛选状态序列化为路由 query（省略默认值，保持 URL 干净） */
export function toQuery(state: QueryState): Record<string, string> {
  const query: Record<string, string> = {};
  if (state.category) query.category = state.category;
  if (state.type) query.type = state.type;
  if (state.q.trim()) query.q = state.q.trim();
  if (state.page > 1) query.page = String(state.page);
  return query;
}

export function hasFilter(state: QueryState): boolean {
  return !!state.category || !!state.type || !!state.q.trim();
}
